import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { formatPrice, waLink } from "../lib/constants";
import { WhatsAppIcon } from "./icons";
import { OrderForm } from "./OrderForm";

export const PerfumeModal = () => {
  const { selected, closePerfume, collections, settings } = useShop();
  const cols = selected ? collections.filter((c) => (selected.collections || []).includes(c.slug)) : [];
  const col = cols[0];

  const orderText = selected
    ? `Bonjour, je souhaite commander le parfum « ${selected.name} » — ${cols.map((c) => c.name).join(" + ")} (${selected.size || "30 ml"}), ${formatPrice(selected.price)} hors frais de livraison. Merci !`
    : "";
  const waHref = selected && settings.whatsapp_number ? waLink(settings.whatsapp_number, orderText) : "";

  const notes = selected
    ? [
        ["Tête", selected.notes?.tete],
        ["Cœur", selected.notes?.coeur],
        ["Fond", selected.notes?.fond],
      ].filter(([, v]) => v)
    : [];

  return (
    <AnimatePresence>
      {selected && (
        <motion.div
          key="perfume-modal"
          data-testid="perfume-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[160] flex items-end justify-center sm:items-center sm:p-6"
        >
          <div className="absolute inset-0 bg-noir/70 backdrop-blur-sm" onClick={closePerfume} data-testid="modal-backdrop" />
          <motion.div
            data-lenis-prevent
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative max-h-[92vh] w-full max-w-4xl overflow-y-auto overscroll-contain rounded-t-3xl bg-cream sm:rounded-2xl"
          >
            <button
              data-testid="modal-close-button"
              onClick={closePerfume}
              aria-label="Fermer"
              className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-ivory/90 text-espresso backdrop-blur transition-colors hover:bg-ivory"
            >
              <X size={18} />
            </button>

            <div className="grid sm:grid-cols-2">
              <div className="relative min-h-[300px] bg-sand sm:min-h-[580px]">
                {selected.image_url && (
                  <img src={selected.image_url} alt={selected.name} className="absolute inset-0 h-full w-full object-cover" />
                )}
              </div>

              <div className="flex flex-col p-7 sm:p-10">
                <p className="eyebrow text-champagne">{col?.name}</p>
                <h2 className="mt-3 font-serif text-4xl leading-none text-espresso sm:text-5xl">{selected.name}</h2>
                <p className="mt-4 text-[15px] leading-relaxed text-espresso/75">
                  {selected.description || selected.short_desc}
                </p>

                {notes.length > 0 && (
                  <div className="mt-6">
                    {notes.map(([label, value]) => (
                      <div key={label} className="flex items-start justify-between gap-6 border-b border-espresso/10 py-3">
                        <span className="pt-0.5 text-[10px] uppercase tracking-[0.25em] text-taupe">{label}</span>
                        <span className="max-w-[62%] text-right text-sm text-espresso/80">{value}</span>
                      </div>
                    ))}
                  </div>
                )}

                {selected.conseils && (
                  <div className="mt-6 rounded-xl border border-espresso/10 bg-ivory/70 p-5" data-testid="perfume-conseils">
                    <p className="eyebrow text-champagne">Conseils d'utilisation</p>
                    <p className="mt-3 text-sm leading-relaxed text-espresso/75">{selected.conseils}</p>
                  </div>
                )}

                <div className="mt-auto pt-7">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <p className="font-serif text-3xl text-espresso">{formatPrice(selected.price)}</p>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-taupe">hors frais de livraison</span>
                  </div>
                  <p className="mt-1 text-xs text-espresso/50">Extrait de parfum · {selected.size} · concentration 30 %</p>

                  <div className="mt-6 flex flex-col gap-3">
                    {waHref && (
                      <a
                        data-testid="modal-whatsapp-button"
                        href={waHref}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-pill w-full bg-espresso py-4 text-ivory hover:bg-noir"
                      >
                        <WhatsAppIcon /> Commander sur WhatsApp
                      </a>
                    )}
                  </div>

                  <OrderForm perfume={selected} />

                  <p data-testid="chogan-shipping-mention" className="mt-5 text-[11px] leading-relaxed text-taupe">
                    Parfums du groupe Chogan — expédiés directement par Chogan : envoi sécurisé, fiable et suivi.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
